// src/api/tasaVenta.js - API para manejo de tasas de venta
import { apiFetch } from './index.js';
import { obtenerFuentePorId } from './fuenteTasa.js';

/**
 * Obtener todas las tasas de venta
 * @param {string} estado - 'all', 'activas', 'inactivas'
 * @returns {Promise} Lista de tasas de venta
 */
export const obtenerTasasVenta = async (estado = 'all') => {
  try {
    console.log('📋 Obteniendo tasas de venta...', { estado });
    
    let endpoint = '/api/TasaCambioVenta';
    
    if (estado === 'activas') {
      endpoint = '/api/TasaCambioVenta/activas';
    }
    
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas de venta obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasas de venta:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasa de venta actual
 * @returns {Promise} Tasa de venta actual
 */
export const obtenerTasaVentaActual = async () => {
  try {
    console.log('💰 Obteniendo tasa de venta actual...');
    
    const response = await apiFetch('/api/TasaCambioVenta/actual');
    
    return {
      success: true,
      data: response,
      message: 'Tasa de venta actual obtenida exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasa de venta actual:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener una tasa de venta por ID
 * @param {number} id - ID de la tasa
 * @returns {Promise} Datos de la tasa
 */
export const obtenerTasaVentaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔍 Obteniendo tasa de venta ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Tasa de venta obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasa de venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener tasas de venta por fuente
 * @param {number} idFuente - ID de la fuente
 * @returns {Promise} Tasas de la fuente
 */
export const obtenerTasasVentaPorFuente = async (idFuente) => {
  try {
    if (!idFuente) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`🌐 Obteniendo tasas de venta de fuente ID: ${idFuente}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/fuente/${idFuente}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas de venta por fuente obtenidas'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas de venta por fuente ${idFuente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener historial de tasas de venta por fuente
 * @param {number} idFuente - ID de la fuente
 * @returns {Promise} Historial de tasas
 */
export const obtenerHistorialTasasVenta = async (idFuente) => {
  try {
    if (!idFuente) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`📊 Obteniendo historial de tasas de venta de fuente ID: ${idFuente}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/fuente/${idFuente}/historial`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Historial de tasas de venta obtenido'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo historial de tasas de venta ${idFuente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasas de venta por fecha
 * @param {string} fecha - Fecha específica (YYYY-MM-DD)
 * @returns {Promise} Tasas de esa fecha
 */
export const obtenerTasasVentaPorFecha = async (fecha) => {
  try {
    if (!fecha) {
      throw new Error('Fecha es requerida');
    }
    
    console.log(`📅 Obteniendo tasas de venta para fecha: ${fecha}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/fecha/${encodeURIComponent(fecha)}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas de venta por fecha obtenidas'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas de venta por fecha ${fecha}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Buscar tasas de venta
 * @param {Object} criterios - Criterios de búsqueda
 * @returns {Promise} Resultados de la búsqueda
 */
export const buscarTasasVenta = async (criterios) => {
  try {
    console.log('🔎 Buscando tasas de venta:', criterios);
    
    const response = await apiFetch('/api/TasaCambioVenta/search', {
      method: 'POST',
      body: JSON.stringify(criterios || {})
    });
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error('❌ Error buscando tasas de venta:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasa de venta con relaciones
 * @param {number} id - ID de la tasa
 * @returns {Promise} Tasa con relaciones
 */
export const obtenerTasaVentaConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔍 Obteniendo tasa de venta con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response,
      message: 'Tasa de venta con relaciones obtenida'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasa de venta con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Comparar una tasa de venta con otras
 * @param {number} id - ID de la tasa a comparar
 * @returns {Promise} Resultado de la comparación
 */
export const compararTasaVenta = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`⚖️ Comparando tasa de venta ID: ${id} con otras`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}/comparar`);
    
    return {
      success: true,
      data: response,
      message: 'Comparación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error comparando tasa de venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si existe una tasa de venta por ID
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la verificación
 */
export const verificarTasaVentaExiste = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔎 Verificando existencia de tasa de venta ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}/exists`);
    
    return {
      success: true,
      existe: response?.existe || false,
      data: response,
      message: 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando tasa de venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Validar spread de tasa de venta
 * @param {Object} tasaData - Datos a validar
 * @returns {Promise} Resultado de la validación
 */
export const validarSpreadTasaVenta = async (tasaData) => {
  try {
    console.log('✅ Validando spread de tasa de venta:', tasaData);
    
    const response = await apiFetch('/api/TasaCambioVenta/validar-spread', {
      method: 'POST',
      body: JSON.stringify(tasaData || {})
    });
    
    return {
      success: true,
      valido: true,
      data: response,
      message: 'Validación de spread completada'
    };
    
  } catch (error) {
    console.error('❌ Error validando spread:', error);
    return {
      success: false,
      valido: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Crear una nueva tasa de venta
 * @param {Object} tasaData - Datos de la tasa
 * @returns {Promise} Tasa creada
 */
export const crearTasaVenta = async (tasaData) => {
  try {
    // Validaciones
    if (!tasaData.precio_usd_bs || tasaData.precio_usd_bs <= 0) {
      throw new Error('El precio en Bs es requerido y debe ser mayor a 0');
    }
    if (!tasaData.idFuenteTasa) {
      throw new Error('La fuente de tasa es requerida');
    }
    
    console.log('➕ Creando nueva tasa de venta:', tasaData);
    
    // Verificar que la fuente existe
    const fuenteResponse = await obtenerFuentePorId(tasaData.idFuenteTasa);
    if (!fuenteResponse.success) {
      throw new Error('La fuente especificada no existe');
    }
    
    const response = await apiFetch('/api/TasaCambioVenta', {
      method: 'POST',
      body: JSON.stringify({
        fecha: tasaData.fecha || new Date().toISOString(),
        precio_usd_bs: parseFloat(tasaData.precio_usd_bs),
        idFuenteTasa: parseInt(tasaData.idFuenteTasa),
        activa: tasaData.activa !== undefined ? tasaData.activa : true,
        observaciones: tasaData.observaciones || null,
        idUsuario: tasaData.idUsuario || null
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa de venta creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando tasa de venta:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una tasa de venta existente
 * @param {number} id - ID de la tasa
 * @param {Object} tasaData - Datos a actualizar
 * @returns {Promise} Tasa actualizada
 */
export const actualizarTasaVenta = async (id, tasaData) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    if (!tasaData.precio_usd_bs || tasaData.precio_usd_bs <= 0) {
      throw new Error('El precio en Bs es requerido y debe ser mayor a 0');
    }
    if (!tasaData.idFuenteTasa) {
      throw new Error('La fuente de tasa es requerida');
    }
    
    console.log(`✏️ Actualizando tasa de venta ID: ${id}`, tasaData);
    
    const updateData = {
      precio_usd_bs: parseFloat(tasaData.precio_usd_bs),
      idFuenteTasa: parseInt(tasaData.idFuenteTasa),
      activa: tasaData.activa !== undefined ? tasaData.activa : true,
      observaciones: tasaData.observaciones || null
    };
    
    if (tasaData.idUsuario) {
      updateData.idUsuario = parseInt(tasaData.idUsuario);
    }
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa de venta actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando tasa de venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar una tasa de venta
 * @param {number} id - ID de la tasa a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarTasaVenta = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🗑️ Eliminando tasa de venta ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa de venta eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando tasa de venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de una tasa de venta
 * @param {number} id - ID de la tasa
 * @param {boolean} activa - Nuevo estado
 * @returns {Promise} Resultado del cambio
 */
export const cambiarEstadoTasaVenta = async (id, activa) => {
  try {
    if (!id || activa === undefined) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de tasa de venta ID: ${id} a ${activa ? 'activa' : 'inactiva'}`);
    
    const response = await apiFetch(`/api/TasaCambioVenta/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({ activa })
    });
    
    return {
      success: true,
      data: response,
      message: 'Estado de tasa de venta actualizado'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de tasa de venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar una tasa de venta
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la activación
 */
export const activarTasaVenta = async (id) => {
  return await cambiarEstadoTasaVenta(id, true);
};

/**
 * Desactivar una tasa de venta
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarTasaVenta = async (id) => {
  return await cambiarEstadoTasaVenta(id, false);
};

/**
 * Convertir moneda usando tasa de venta
 * @param {number} monto - Monto a convertir
 * @param {string} direccion - 'usd_to_bs' o 'bs_to_usd'
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMonedaVenta = async (monto, direccion = 'usd_to_bs') => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto válido es requerido');
    }
    
    console.log(`💱 Convirtiendo ${monto} (${direccion}) con tasa de venta`);
    
    const response = await apiFetch('/api/TasaCambioVenta/convertir', {
      method: 'POST',
      body: JSON.stringify({
        monto: parseFloat(monto),
        tipoOperacion: direccion
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Conversión realizada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Convertir moneda con tasa personalizada
 * @param {number} monto - Monto a convertir
 * @param {number} tasa - Tasa personalizada
 * @param {string} direccion - 'usd_to_bs' o 'bs_to_usd'
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMonedaVentaConTasa = async (monto, tasa, direccion = 'usd_to_bs') => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto válido es requerido');
    }
    if (!tasa || tasa <= 0) {
      throw new Error('Tasa válida es requerida');
    }
    
    console.log(`💱 Convirtiendo ${monto} con tasa personalizada ${tasa} (${direccion})`);
    
    const response = await apiFetch('/api/TasaCambioVenta/convertir-con-tasa', {
      method: 'POST',
      body: JSON.stringify({
        monto: parseFloat(monto),
        tasaPersonalizada: parseFloat(tasa),
        tipoOperacion: direccion
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Conversión personalizada realizada'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda con tasa personalizada:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener reporte de tasas de venta
 * @param {Object} parametros - Parámetros del reporte
 * @returns {Promise} Reporte generado
 */
export const obtenerReporteTasasVenta = async (parametros = {}) => {
  try {
    console.log('📊 Obteniendo reporte de tasas de venta:', parametros);
    
    const queryParams = new URLSearchParams(parametros).toString();
    const url = `/api/TasaCambioVenta/reporte${queryParams ? `?${queryParams}` : ''}`;
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      message: 'Reporte obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo reporte de tasas de venta:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Formatear precio de tasa de venta
 * @param {number} precio - Precio a formatear
 * @returns {string} Precio formateado
 */
export const formatearPrecioVenta = (precio) => {
  if (precio === null || precio === undefined) return 'N/A';
  return `${precio.toFixed(2)} Bs/USD`;
};

/**
 * Calcular monto en Bs a partir de USD
 * @param {number} usd - Cantidad de USD
 * @param {number} tasa - Tasa de venta
 * @returns {number} Monto en Bs
 */
export const calcularBsDesdeUsd = (usd, tasa) => {
  if (!usd || !tasa) return 0;
  return usd * tasa;
};

/**
 * Calcular monto en USD a partir de Bs
 * @param {number} bs - Cantidad en Bs
 * @param {number} tasa - Tasa de venta
 * @returns {number} Monto en USD
 */
export const calcularUsdDesdeBs = (bs, tasa) => {
  if (!bs || !tasa) return 0;
  return bs / tasa;
};

/**
 * Calcular spread entre tasas de compra y venta
 * @param {number} tasaCompra - Tasa de compra
 * @param {number} tasaVenta - Tasa de venta
 * @returns {Object} Spread calculado
 */
export const calcularSpread = (tasaCompra, tasaVenta) => {
  if (!tasaCompra || !tasaVenta) {
    return { valor: 0, porcentaje: 0 };
  }
  
  const spread = tasaVenta - tasaCompra;
  const porcentaje = (spread / tasaCompra) * 100;
  
  return {
    valor: parseFloat(spread.toFixed(2)),
    porcentaje: parseFloat(porcentaje.toFixed(2))
  };
};

export default {
  obtenerTasasVenta,
  obtenerTasaVentaActual,
  obtenerTasaVentaPorId,
  obtenerTasasVentaPorFuente,
  obtenerHistorialTasasVenta,
  obtenerTasasVentaPorFecha,
  buscarTasasVenta,
  obtenerTasaVentaConRelaciones,
  compararTasaVenta,
  verificarTasaVentaExiste,
  validarSpreadTasaVenta,
  crearTasaVenta,
  actualizarTasaVenta,
  eliminarTasaVenta,
  activarTasaVenta,
  desactivarTasaVenta,
  cambiarEstadoTasaVenta,
  convertirMonedaVenta,
  convertirMonedaVentaConTasa,
  obtenerReporteTasasVenta,
  formatearPrecioVenta,
  calcularBsDesdeUsd,
  calcularUsdDesdeBs,
  calcularSpread
};